import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "~db/db";
import { initialPath } from "~misc/paths";
import { useNavigate } from "react-router-dom";
import useCheckIn from "./useCheckIn";
import { todasAsDatasDoMes } from "~services/GlobalServices";
import useLocalStorage from "~hooks/useLocalStorage";
import { id } from "date-fns/locale";
import { getDeviceType } from "~services/utils/deviceInfo";
import appInsights from "~/appInsights";
import { getLocation } from "~services/utils/geoLocation";
import useUsuario from "~/fetchs/useUsuario";
import { useSetLogAcesso } from "~hooks/useBackOffice";

const TELEMETRY_SOURCE = "ROTEIRIZACAO_VISITA";

const getCodigoCliente = (cliente, idClient) => {
  return cliente?.codigoCliente ?? cliente?.codigo ?? cliente?.clienteCodigo ?? cliente?.codCliente ?? cliente?.id ?? idClient ?? null;
};

const getCodigoUsuario = (usuario) => {
  return usuario?.codigo ?? usuario?.codigoUsuario ?? usuario?.usuarioCodigo ?? usuario?.id ?? null;
};


export const marcarRotaComoVisitada = async ({ clienteId, localId }) => {
  if (clienteId == null) return;

  try {
    const dataAtual = new Date().toLocaleDateString("pt-BR");
    const datas = todasAsDatasDoMes();

    const rotasDoCliente = await db.rotas
      .filter((r) => r.clienteId == clienteId && (datas || []).includes(r.dataLabel))
      .toArray();

    const rota = rotasDoCliente.find((r) => r.dataLabel === dataAtual) ?? rotasDoCliente[0];

    if (!rota) return;

    const local = localId == null ? null : await db.local.get(Number(localId));

    await db.rotas.update(rota.id, {
      visitado: true,
      visita: {
        ...(rota.visita ?? {}),
        dataLabel: dataAtual,
        data: new Date().toISOString(),
        status: 1,
        local: local?.nome ?? rota.visita?.local ?? null,
      },
    });
  } catch (error) {

    console.error("Erro ao marcar rota como visitada:", error);
  }
};

const useVisita = ({ idClient, salvarVisita, setLoading, onSuccess }) => {
  const navigate = useNavigate();
  const { usuario } = useUsuario();

  const [dispositivo, setDispositivo] = useState("");

  useEffect(() => {
    const detectado = getDeviceType();
    setDispositivo(detectado);
  }, []);

  const [linkOnSuccess, setLinkOnSuccess] = useState("");
  const [enviaBack, setEnviaBack] = useState(false);

  const [rota, setRota] = useState();

  const { checkMissao, checkin, checkout } = useCheckIn();

  const marcaMissoes = (missoes) => {
    if (checkMissao) {
      db.visitaAtiva.update(3, { missoes });
    } else {
      db.visitaAtiva.put({
        id: 3,
        missoes,
      });
    }
  };

  const [_, setLocalStorage] = useLocalStorage("loc", null);

  const visitarRota = () => {
    setLoading(true);
    db.visitaAtiva.clear();

    getLocation()
      .then((location) => {
        if (!location?.latitude || !location?.longitude || location?.latitude == null || location?.longitude == null) {
          setLocalStorage({ status: true, id: id });
        }
        db.visitaAtiva
          .add({
            id: 1,
            latitude: location?.latitude ?? null,
            longitude: location?.longitude ?? null,
            precisao: location?.precisaoMetros ?? null,
            clienteId: idClient,
            data: new Date(),
            status: 0,
          })
          .then(() => {
            setLoading(false);
            navigate(`${initialPath}/roteirizacao/${idClient}`);
            setLocalStorage({ status: false, id: idClient });
          })
          .catch((error) => {
            console.error("Erro ao salvar visita no IndexedDB:", error);
            setLoading(false);
          });
      })
      .catch((error) => {
        console.error("Erro ao obter localização:", error);
        setLoading(false);
      });
  };

  const cancelarVisita = () => {
    db.visitaAtiva.clear();
    navigate(`${initialPath}/roteirizacao/mapa`);
  };

  const cancelarVisitaListagem = () => {
    db.visitaAtiva.clear();
  };

  const [dadosLoc, setDadosLoc] = useState({});
  const [telemetryContext, setTelemetryContext] = useState({
    codigoCliente: null,
    codigoUsuario: null,
  });
  const [logFinalizacao, setLogFinalizacao] = useState(null);

  const saveLogFinalizacao = useSetLogAcesso(logFinalizacao?.location ?? {}, logFinalizacao?.pagina ?? "", dispositivo);

  useEffect(() => {
    if (logFinalizacao?.pagina) {
      saveLogFinalizacao();
    }
  }, [logFinalizacao]);

  const getCodigoClientePagina = async (codigoClienteFallback, clienteIdFallback) => {
    if (codigoClienteFallback && String(codigoClienteFallback) !== String(clienteIdFallback)) {
      return codigoClienteFallback;
    }

    try {
      const clienteDb = await db.cliente.get(clienteIdFallback);
      return clienteDb?.codigo ?? clienteDb?.codigoCliente ?? codigoClienteFallback ?? clienteIdFallback;
    } catch {
      return codigoClienteFallback ?? clienteIdFallback;
    }
  };

  const removeRotasDoLocalStorage = () => {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("route_")) {
        localStorage.removeItem(key);
      }
    }
  };
  const finalizarVisita = async (values, cliente, confirmacao) => {
    if (setLoading && typeof setLoading === "function") setLoading(true);

    await getLocation().then((location) => {
      db.visitaAtiva.put({
        id: 2,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        precisao: location?.precisaoMetros ?? null,
        clienteId: idClient,
        data: new Date(),
        status: 1,
        localId: values.localId,
      });

      setDadosLoc((prevState) => ({
        ...prevState,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        precisao: location?.precisaoMetros ?? null,
      }));
      removeRotasDoLocalStorage();

      const trilhaNome = cliente?.trilha?.nome?.toUpperCase();
      const codigoCliente = getCodigoCliente(cliente, idClient);
      const codigoUsuario = getCodigoUsuario(usuario);

      setTelemetryContext({ codigoCliente, codigoUsuario });

      try {
        let storagePesquisas = null;

        try {
          storagePesquisas = JSON.parse(localStorage.getItem("cliente-pesquisas-mm") || "{}");
        } catch (parseError) {
          appInsights.trackTrace({
            message: `[${TELEMETRY_SOURCE}] FinalizarVisitaPesquisaStorageParseErro ClienteId=${idClient}`,
            severityLevel: 2,
            properties: {
              traceSource: TELEMETRY_SOURCE,
              traceEvent: "FINALIZAR_VISITA_STORAGE_PARSE_ERROR",
              clienteId: idClient,
              codigoCliente: codigoCliente,
              codigoUsuario: codigoUsuario,
              trilha: trilhaNome ?? "SEM_TRILHA",
              erroMensagem: parseError?.message ?? "Erro ao fazer parse do localStorage",
              dataEnvio: new Date().toISOString(),
            },
          });
        }

        appInsights.trackTrace({
          message: `[${TELEMETRY_SOURCE}] FinalizarVisita ClienteId=${idClient} PesquisaId=${values?.contextoId ?? "NA"}`,
          severityLevel: 1,
          properties: {
            traceSource: TELEMETRY_SOURCE,
            traceEvent: "FINALIZAR_VISITA",
            traceSearch: `cliente:${idClient}|codigoCliente:${codigoCliente ?? "NA"}|usuario:${codigoUsuario ?? "NA"}|pesquisa:${values?.contextoId ?? "NA"}|local:${values?.localId ?? "NA"}`,
            clienteId: idClient,
            codigoCliente: codigoCliente,
            codigoUsuario: codigoUsuario,
            trilha: trilhaNome ?? "SEM_TRILHA",
            confirmacao: String(confirmacao),
            pesquisaId: values?.contextoId ?? null,
            localId: values?.localId ?? null,
            values,
            pesquisasMapaMina: storagePesquisas,
            latitude: location?.latitude ?? null,
            longitude: location?.longitude ?? null,
            precisao: location?.precisaoMetros ?? null,
            dataEnvio: new Date().toISOString(),
          },
        });
      } catch (error) {
        console.error("Erro ao registrar trace no AppInsights", error);
        appInsights.trackException({
          exception: error,
          severityLevel: 3,
          properties: {
            traceSource: TELEMETRY_SOURCE,
            traceEvent: "FINALIZAR_VISITA_TRACE_ERROR",
            clienteId: cliente?.id ?? null,
            codigoCliente: codigoCliente,
            codigoUsuario: codigoUsuario,
          },
        });
      }
    });
  };

  useEffect(() => {
    if (
      checkout &&
      (!checkout.latitude || !checkout.longitude || !checkout.precisao) &&
      (location?.latitude || location?.longitude || location?.precisaoMetros)
    ) {
      db.visitaAtiva.update(2, {
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        precisao: location?.precisaoMetros ?? null,
      });
    }
  }, [location, checkout]);

  useEffect(() => {
    if (checkout) {
      setLinkOnSuccess(`${initialPath}/roteirizacao/mapa`);
      setEnviaBack(true);
    }
  }, [checkout]);

  const visitaBody = useMemo(() => {
    if (checkout && checkin && checkMissao && dispositivo) {
      return {
        dispositivo: dispositivo,
        checkIn: {
          latitude: checkin.latitude,
          longitude: checkin.longitude,
          precisao: checkin.precisao,
          clienteId: checkin.clienteId,
          data: new Date(checkin.data.getTime() - checkin.data.getTimezoneOffset() * 60000).toJSON(),
          status: checkin.status,
          missoes: checkMissao?.missoes,
        },
        checkOut: {
          latitude: checkout.latitude,
          longitude: checkout.longitude,
          precisao: checkout.precisao,
          clienteId: checkout.clienteId,
          data: new Date(checkout.data.getTime() - checkout.data.getTimezoneOffset() * 60000).toJSON(),
          status: checkout.status,
          localId: checkout.localId,
        },
      };
    }
  }, [checkout, checkin, checkMissao, dispositivo]);

  useEffect(() => {
    const fetchRota = async () => {
      if (checkin) {
        const datas = todasAsDatasDoMes();
        const rotaAtiva = await db.rotas
          .filter((r) => r.clienteId == checkin.clienteId && (datas || []).includes(r.dataLabel))
          .first();
        setRota(rotaAtiva);
      }
    };

    if (!rota) fetchRota();
  }, [checkin, visitaBody, checkout]);

  const hasSaved = useRef(false);

  useEffect(() => {
    if (enviaBack && visitaBody && !hasSaved.current) {
      hasSaved.current = true; // Marca como executado

      const enviarVisita = async () => {
        const codigoClienteAtual = telemetryContext?.codigoCliente ?? visitaBody?.checkOut?.clienteId ?? idClient ?? null;
        const codigoUsuarioAtual = telemetryContext?.codigoUsuario ?? getCodigoUsuario(usuario);

        try {
          if (typeof salvarVisita == "function") {
            await Promise.resolve(salvarVisita(visitaBody));

            await marcarRotaComoVisitada({
              clienteId: visitaBody?.checkOut?.clienteId ?? idClient,
              localId: visitaBody?.checkOut?.localId,
            });

            const codigoClientePagina = await getCodigoClientePagina(codigoClienteAtual, visitaBody?.checkOut?.clienteId ?? idClient);
            setLogFinalizacao({
              pagina: `Roteirização > Cliente ${codigoClientePagina} > Visita (Enviada com sucesso)`,
              location: {
                latitude: visitaBody?.checkOut?.latitude ?? null,
                longitude: visitaBody?.checkOut?.longitude ?? null,
                precisaoMetros: visitaBody?.checkOut?.precisao ?? null,
              },
            });

            appInsights.trackTrace({
              message: `[${TELEMETRY_SOURCE}] EnvioVisitaSucesso ClienteId=${visitaBody?.checkOut?.clienteId ?? idClient}`,
              severityLevel: 1,
              properties: {
                traceSource: TELEMETRY_SOURCE,
                traceEvent: "ENVIO_VISITA_SUCESSO",
                traceSearch: `cliente:${visitaBody?.checkOut?.clienteId ?? idClient}|codigoCliente:${codigoClienteAtual ?? "NA"}|usuario:${codigoUsuarioAtual ?? "NA"}|local:${visitaBody?.checkOut?.localId ?? "NA"}`,
                clienteId: visitaBody?.checkOut?.clienteId ?? idClient,
                codigoCliente: codigoClienteAtual,
                codigoUsuario: codigoUsuarioAtual,
                localId: visitaBody?.checkOut?.localId ?? null,
                checkinData: visitaBody?.checkIn?.data ?? null,
                checkoutData: visitaBody?.checkOut?.data ?? null,
                dataEnvio: new Date().toISOString(),
              },
            });
          }
        } catch (error) {
          console.error("Erro ao enviar visita:", error);

          const codigoClientePagina = await getCodigoClientePagina(codigoClienteAtual, visitaBody?.checkOut?.clienteId ?? idClient);
          setLogFinalizacao({
            pagina: `Roteirização > Cliente ${codigoClientePagina} > Visita (Erro ao enviar )`,
            location: {
              latitude: visitaBody?.checkOut?.latitude ?? null,
              longitude: visitaBody?.checkOut?.longitude ?? null,
              precisaoMetros: visitaBody?.checkOut?.precisao ?? null,
            },
          });

          appInsights.trackTrace({
            message: `[${TELEMETRY_SOURCE}] EnvioVisitaErro ClienteId=${visitaBody?.checkOut?.clienteId ?? idClient}`,
            severityLevel: 3,
            properties: {
              traceSource: TELEMETRY_SOURCE,
              traceEvent: "ENVIO_VISITA_ERRO",
              traceSearch: `cliente:${visitaBody?.checkOut?.clienteId ?? idClient}|codigoCliente:${codigoClienteAtual ?? "NA"}|usuario:${codigoUsuarioAtual ?? "NA"}|local:${visitaBody?.checkOut?.localId ?? "NA"}`,
              clienteId: visitaBody?.checkOut?.clienteId ?? idClient,
              codigoCliente: codigoClienteAtual,
              codigoUsuario: codigoUsuarioAtual,
              localId: visitaBody?.checkOut?.localId ?? null,
              erroMensagem: error?.message ?? "Erro desconhecido ao enviar visita",
              erroStack: error?.stack ?? null,
              dataEnvio: new Date().toISOString(),
            },
          });
          appInsights.trackException({
            exception: error,
            severityLevel: 3,
            properties: {
              traceSource: TELEMETRY_SOURCE,
              traceEvent: "ENVIO_VISITA_ERRO",
              clienteId: visitaBody?.checkOut?.clienteId ?? idClient,
              codigoCliente: codigoClienteAtual,
              codigoUsuario: codigoUsuarioAtual,
              localId: visitaBody?.checkOut?.localId ?? null,
            },
          });
        } finally {
          if (setLoading && typeof setLoading == "function") setLoading(false);
        }
      };

      enviarVisita();
    }
  }, [enviaBack, visitaBody]);

  return {
    checkin,
    checkout,
    visitarRota,
    cancelarVisita,
    cancelarVisitaListagem,
    finalizarVisita,
    linkOnSuccess,
    marcaMissoes,
    dadosLoc,
  };
};

export default useVisita;
